import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import ShareStrip from "@/components/common/ShareStrip";
import {
  X,
  ArrowRight,
  ArrowLeft,
  Send,
  Loader2,
  Check,
  Compass,
  Sparkles,
} from "lucide-react";

/* ============================================================
   EmailJS configuration
   Template variables sent: {{lead_name}} {{lead_email}}
   {{lead_phone}} {{lead_message}} {{package_name}}
   {{package_price}} {{quiz_answers}} {{submitted_at}}
   ============================================================ */
const EMAILJS_SERVICE_ID = "service_bzcnx6c";
const EMAILJS_TEMPLATE_ID = "template_9qj9wbm";
const EMAILJS_PUBLIC_KEY = "TWsB8Hcu97XViHXrF";

const QUESTIONS = [
  {
    id: "q1",
    title: "Which best describes you?",
    options: [
      { label: "Content creator or podcaster", value: "creator" },
      { label: "Business / brand owner", value: "brand" },
      { label: "Spotlight guest hopeful", value: "guest" },
      { label: "Just exploring", value: "explorer" },
    ],
  },
  {
    id: "q2",
    title: "How often do you plan to create content?",
    options: [
      { label: "Weekly — I publish every week", value: "weekly" },
      { label: "A few times a month", value: "monthly" },
      { label: "Once in a while / one-time project", value: "occasional" },
      { label: "This is my first time", value: "first" },
    ],
  },
  {
    id: "q3",
    title: "How much production help do you need?",
    options: [
      { label: "Just the space and gear — I'll edit myself", value: "space" },
      { label: "Editing + ready-to-post clips", value: "edit" },
      { label: "Run the entire content engine for me", value: "full" },
      { label: "I want to test the studio first", value: "test" },
    ],
  },
  {
    id: "q4",
    title: "What's your budget for this?",
    options: [
      { label: "Under $200 (just testing the waters)", value: "low" },
      { label: "$500 – $1,000 per session", value: "mid" },
      { label: "$1,000 – $2,000 per month", value: "high" },
      { label: "$2,000+ — let's go all in", value: "premium" },
    ],
  },
  {
    id: "q5",
    title: "What's your #1 goal right now?",
    options: [
      { label: "Launch or scale a podcast", value: "podcast" },
      { label: "Build my brand on social", value: "social" },
      { label: "Professional headshots & brand photos", value: "shoot" },
      { label: "Hands-off — be everywhere on social", value: "handsoff" },
      { label: "Just learning what you offer", value: "learn" },
    ],
  },
];

const PACKAGES = {
  test_run: {
    name: "Test Run",
    tagline: "Starter · No-risk trial",
    price: "$150 / session",
    why: "You're new here. Try the studio — same gear, 30-min session, same-day clip.",
    accent: "#00f0ff",
  },
  studio_day: {
    name: "Studio Day",
    tagline: "Creator · Just need the space",
    price: "$605 / session",
    why: "You bring the show, we bring the room. Multi-cam 4K, pro audio, lights — you handle the edit.",
    accent: "#a395c3",
  },
  creator_launch: {
    name: "Creator Launch",
    tagline: "Creator · Most popular",
    price: "$1,045 / episode",
    why: "Record + edited episode + 3 short-form clips + branded intro/outro. Ready to publish.",
    accent: "#00f0ff",
  },
  content_partner: {
    name: "Content Partner",
    tagline: "Creator · Monthly retainer",
    price: "$1,540 / month",
    why: "Consistent weekly publishing demands a partner — sessions, edits, clips, priority scheduling.",
    accent: "#ff0055",
  },
  brand_shoot: {
    name: "Brand Shoot Day",
    tagline: "Brand · One-time shoot",
    price: "$850 / one-time",
    why: "A single shoot that gives you a full library of headshots, product, and short-form clips.",
    accent: "#a395c3",
  },
  content_engine: {
    name: "Content Engine",
    tagline: "Brand · Monthly content",
    price: "$1,500 / month",
    why: "A month of ready-to-post content, every month — produced by us, posted by you.",
    accent: "#00f0ff",
  },
  social_command: {
    name: "Social Command",
    tagline: "Brand · Full management",
    price: "$3,000 / month",
    why: "Hands-off media for your brand — we shoot, edit, post, and manage it all.",
    accent: "#ff0055",
  },
};

function recommend(answers) {
  const { q1, q2, q3, q4 } = answers;
  // Lowest-friction trial wins when explicitly chosen or budget is tight
  if (q3 === "test" || q4 === "low" || q2 === "first" || q1 === "explorer") {
    return "test_run";
  }
  if (q1 === "creator") {
    if (q3 === "space") return "studio_day";
    if (q3 === "full" || q2 === "weekly") return "content_partner";
    return "creator_launch";
  }
  if (q1 === "brand") {
    if (q3 === "full" || q4 === "premium") return "social_command";
    if (q2 === "weekly" || q2 === "monthly") return "content_engine";
    return "brand_shoot";
  }
  // Spotlight guest path → Test Run as soft intro
  if (q1 === "guest") return "test_run";
  return "creator_launch";
}

const PackageQuiz = ({ open, onClose }) => {
  const [step, setStep] = useState(0); // 0..4 questions, 5 = contact, 6 = result
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  const reset = () => {
    setStep(0);
    setAnswers({});
    setContact({ name: "", email: "", phone: "", message: "" });
    setSubmitting(false);
    setDone(false);
  };

  const handleClose = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const choose = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    // auto-advance after micro pause
    setTimeout(() => setStep((s) => Math.min(s + 1, QUESTIONS.length)), 220);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const recommendedKey = recommend(answers);
  const recommended = PACKAGES[recommendedKey];

  const labelFor = (qid) => {
    const q = QUESTIONS.find((x) => x.id === qid);
    if (!q) return "";
    const opt = q.options.find((o) => o.value === answers[qid]);
    return opt ? opt.label : "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      toast.error("Add your name and email so we can send the recommendation.");
      return;
    }
    setSubmitting(true);
    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: EMAILJS_PUBLIC_KEY },
      );
      setDone(true);
      setStep(QUESTIONS.length + 1); // result page
    } catch (err) {
      // Common cause: placeholder keys not replaced yet
      const msg =
        err?.text ||
        err?.message ||
        "Email service not configured yet. Showing your recommendation below.";
      // Still show the recommendation locally so the experience completes
      setDone(true);
      setStep(QUESTIONS.length + 1);
      toast.message("Couldn't send email", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = QUESTIONS.length + 1; // 5 questions + 1 contact step
  const progressPct = Math.min(100, Math.round((step / totalSteps) * 100));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          data-testid="package-quiz-modal"
        >
          <ShareStrip campaign="package_quiz" title="Find your SB Media package" text="60-second quiz to match your goals with the right studio package." href="/#packages" className="mr-1" />
          <button
            aria-label="Close"
            onClick={handleClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md cursor-default"
          />

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#0a0612] border border-white/10 glow-border"
          >
            <div className="sticky top-0 z-10 bg-[#0a0612] px-7 pt-7 pb-5 rounded-t-3xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
                    <Compass size={12} /> Package finder
                  </div>
                  <h3 className="font-display font-black text-2xl md:text-3xl mt-2 tracking-tight">
                    {done
                      ? "Your recommendation."
                      : step < QUESTIONS.length
                        ? "Let's match you up."
                        : "Where should we send it?"}
                  </h3>
                </div>
                <button
                  onClick={handleClose}
                  data-testid="quiz-close"
                  aria-label="Close"
                  className="flex-shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
                >
                  <X size={16} />
                </button>
              </div>
              {!done && (
                <div className="mt-5 h-1 w-full rounded-full bg-white/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-[#00f0ff]"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.45 }}
                    data-testid="quiz-progress"
                  />
                </div>
              )}
            </div>

            <div className="px-7 pb-7">
              <AnimatePresence mode="wait">
                {/* Question steps */}
                {step < QUESTIONS.length && (
                  <motion.div
                    key={`q-${step}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35 }}
                    data-testid={`quiz-step-${step}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                      Question {step + 1} of {QUESTIONS.length}
                    </div>
                    <div className="font-display font-bold text-xl md:text-2xl mt-3 mb-6 leading-snug">
                      {QUESTIONS[step].title}
                    </div>
                    <div className="space-y-2.5">
                      {QUESTIONS[step].options.map((opt) => {
                        const selected =
                          answers[QUESTIONS[step].id] === opt.value;
                        return (
                          <button
                            key={opt.value}
                            data-testid={`quiz-option-${QUESTIONS[step].id}-${opt.value}`}
                            onClick={() => choose(QUESTIONS[step].id, opt.value)}
                            className={`w-full text-left flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border transition-all ${
                              selected
                                ? "bg-[#00f0ff]/10 border-[#00f0ff]/50 text-white"
                                : "bg-white/[0.03] border-white/10 text-white/85 hover:border-white/25 hover:bg-white/[0.06]"
                            }`}
                          >
                            <span className="font-display text-[15px]">
                              {opt.label}
                            </span>
                            <span
                              className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                selected
                                  ? "border-[#00f0ff] bg-[#00f0ff]"
                                  : "border-white/25"
                              }`}
                            >
                              {selected && (
                                <Check
                                  size={12}
                                  className="text-[#05050a]"
                                  strokeWidth={3}
                                />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {step > 0 && (
                      <button
                        onClick={goBack}
                        data-testid="quiz-back"
                        className="mt-6 inline-flex items-center gap-2 text-xs text-white/50 hover:text-white"
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                    )}
                  </motion.div>
                )}

                {/* Contact step */}
                {step === QUESTIONS.length && !done && (
                  <motion.form
                    key="contact"
                    ref={formRef}
                    onSubmit={submit}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.35 }}
                    data-testid="quiz-contact-step"
                    className="space-y-4"
                  >
                    {/* Hidden fields populated for EmailJS template */}
                    <input type="hidden" name="lead_name" value={contact.name} />
                    <input
                      type="hidden"
                      name="lead_email"
                      value={contact.email}
                    />
                    <input
                      type="hidden"
                      name="lead_phone"
                      value={contact.phone}
                    />
                    <input
                      type="hidden"
                      name="lead_message"
                      value={contact.message}
                    />
                    <input
                      type="hidden"
                      name="package_name"
                      value={recommended.name}
                    />
                    <input
                      type="hidden"
                      name="package_price"
                      value={recommended.price}
                    />
                    <input
                      type="hidden"
                      name="quiz_answers"
                      value={QUESTIONS.map(
                        (q, i) =>
                          `${i + 1}. ${q.title} — ${labelFor(q.id) || "—"}`,
                      ).join("\n")}
                    />
                    <input
                      type="hidden"
                      name="submitted_at"
                      value={new Date().toISOString()}
                    />

                    <p className="text-[#a395c3] text-sm leading-relaxed">
                      We&apos;ll send your custom recommendation + next steps
                      straight to your inbox.
                    </p>

                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                        Your name
                      </label>
                      <input
                        type="text"
                        data-testid="quiz-name"
                        value={contact.name}
                        onChange={(e) =>
                          setContact((c) => ({ ...c, name: e.target.value }))
                        }
                        placeholder="Alex Rivera"
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        data-testid="quiz-email"
                        value={contact.email}
                        onChange={(e) =>
                          setContact((c) => ({ ...c, email: e.target.value }))
                        }
                        placeholder="you@brand.com"
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                        Phone (optional)
                      </label>
                      <input
                        type="tel"
                        data-testid="quiz-phone"
                        value={contact.phone}
                        onChange={(e) =>
                          setContact((c) => ({ ...c, phone: e.target.value }))
                        }
                        placeholder="+1 (555) 010-0123"
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                        Anything else we should know? (optional)
                      </label>
                      <textarea
                        data-testid="quiz-message"
                        value={contact.message}
                        onChange={(e) =>
                          setContact((c) => ({
                            ...c,
                            message: e.target.value,
                          }))
                        }
                        placeholder="Timing, project details, anything that helps us prepare..."
                        rows={3}
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl px-4 py-3 text-[15px] text-white placeholder:text-white/30 transition-all resize-none"
                      />
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2">
                      <button
                        type="button"
                        onClick={goBack}
                        data-testid="quiz-contact-back"
                        className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white"
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="quiz-submit"
                        className="group inline-flex items-center justify-center gap-3 pl-6 pr-3 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold hover:bg-white transition-colors disabled:opacity-60"
                      >
                        {submitting ? (
                          <>
                            Sending
                            <Loader2 size={16} className="animate-spin" />
                          </>
                        ) : (
                          <>
                            See my recommendation
                            <span className="w-9 h-9 rounded-full bg-[#05050a] text-[#00f0ff] flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                              <Send size={14} />
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.form>
                )}

                {/* Result */}
                {done && (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                    data-testid="quiz-result"
                  >
                    <div className="rounded-2xl p-7 relative overflow-hidden bg-[#11091a] border border-[#00f0ff]/30">
                      <div
                        className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl"
                        style={{ background: recommended.accent + "33" }}
                      />
                      <div className="relative">
                        <div
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border"
                          style={{
                            color: recommended.accent,
                            borderColor: `${recommended.accent}40`,
                            background: `${recommended.accent}12`,
                          }}
                        >
                          <Sparkles size={11} /> {recommended.tagline}
                        </div>
                        <div className="font-display font-black text-3xl md:text-4xl mt-4 tracking-tighter">
                          {recommended.name}
                        </div>
                        <div className="font-mono-tight text-sm text-white/70 mt-1">
                          {recommended.price}
                        </div>
                        <p className="mt-5 text-[#cfc4e0] text-sm leading-relaxed">
                          {recommended.why}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 p-4 rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/30 text-sm text-[#bff5d0] flex items-start gap-3">
                      <Check
                        size={16}
                        className="text-[#22c55e] mt-0.5 flex-shrink-0"
                      />
                      <span>
                        Recommendation saved & sent to{" "}
                        <span className="text-white font-semibold">
                          {contact.email}
                        </span>
                        . Reply to that email and we&apos;ll lock in your
                        session.
                      </span>
                    </div>

                    <div className="mt-6 flex flex-col md:flex-row gap-3">
                      <a
                        href="#contact"
                        onClick={handleClose}
                        data-testid="quiz-result-book"
                        className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors"
                      >
                        Book this package <ArrowRight size={14} />
                      </a>
                      
                      <button
                        onClick={reset}
                        data-testid="quiz-result-retake"
                        className="inline-flex items-center justify-center gap-2 py-3 px-5 rounded-full bg-white/5 border border-white/10 text-sm hover:bg-white/10"
                      >
                        Retake quiz
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PackageQuiz;
